import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/divider/divider.js';
import '@awesome.me/webawesome/dist/components/drawer/drawer.js';
import '@awesome.me/webawesome/dist/components/popover/popover.js';
import '@awesome.me/webawesome/dist/components/spinner/spinner.js';
import '@awesome.me/webawesome/dist/components/tooltip/tooltip.js';
import '@awesome.me/webawesome/dist/styles/webawesome.css';
import './area_state_legend.js';
import './brand_mark.js';
import './color_scheme_switcher.js';
import './counter_badge.js';
import './menu_toggle_icon.js';
import './notification_dot.js';
import './unsaved_changes_dialog.js';

import {
    faAngleDown,
    faAngleUp,
    faBars,
    faCalendarDays,
    faChevronRight,
    faListUl,
    faPlus,
    faSeedling,
} from '@fortawesome/free-solid-svg-icons';
import { Task } from '@lit/task';
import { css, html, LitElement, nothing, type TemplateResult, unsafeCSS } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import {
    getFarmById,
    type IFarmListItem,
    listAccessibleFarms,
} from '../libraries/api_client/farm.js';
import {
    type IFertilizerYear,
    type IFertilizerYearAreaState,
    listFertilizerYearAreaStates,
    listFertilizerYearsByFarmId,
} from '../libraries/api_client/fertilizer_year.js';
import {
    type IOrganizationListItem,
    listOrganizations,
} from '../libraries/api_client/organization.js';
import { valueOrThrow } from '../libraries/api_result.js';
import { getUserInfo, type IUserInfo, logout } from '../libraries/auth.js';
import { mobileMediaQuery } from '../libraries/breakpoints.js';
import { countedNoun } from '../libraries/counted_noun.js';
import { nameDialog } from '../libraries/dialog_accessible_name.js';
import { filterInactive, hydrateDisplaySettingsOnce } from '../libraries/display_settings.js';
import { DisplaySettingsController } from '../libraries/display_settings_controller.js';
import { isPlainFollow } from '../libraries/event_target.js';
import { requestFertilizerYearCreation } from '../libraries/events.js';
import { deactivatedFarmTooltip } from '../libraries/farm_copy.js';
import { farmPath, farmsPath } from '../libraries/farm_paths.js';
import { areaStateDescriptors, areaStateOf } from '../libraries/fertilizer_year_area_states.js';
import { fertilizerYearAreaEntries } from '../libraries/fertilizer_year_areas.js';
import { pendingInvitationNoun } from '../libraries/invitation_copy.js';
import { getPendingInvitationCount } from '../libraries/invitations_store.js';
import { forgetListPages } from '../libraries/list_state_store.js';
import { permissionsCacheBustKey } from '../libraries/local_storage.js';
import { confirmLeaving, redirectAway, redirectTo } from '../libraries/navigation.js';
import {
    type INavigationEntry,
    isNavigationEntryActive,
    isNavigationEntryVisible,
} from '../libraries/navigation_entries.js';
import { organizationTypeIcon } from '../libraries/organization_type.js';
import {
    type AdministrationRole,
    getPermissions,
    hasOrganizationAccess,
    permissionsChangedEventName,
} from '../libraries/permissions.js';
import { resetSessionCaches } from '../libraries/session.js';
import { readSidebarCollapsed, writeSidebarCollapsed } from '../libraries/sidebar_preference.js';
import { formatDisplayName } from '../libraries/user.js';
import { versionLabel } from '../libraries/version_label.js';
import { onWindowEvent, WindowEventsController } from '../libraries/window_events.js';
import type { FmColorSchemeSwitcher } from './color_scheme_switcher.js';

/**
 * Sidebar context level derived from the URL path. Determines which sidebar
 * row renders in the "aktiv" state: the Organisation row for org-level routes
 * (`/organizations`, `/organizations/:id`, tabs of org-details), the Betrieb
 * row for farm-level routes (`/farms`, `/farms/new`, `/farms/:id`, tabs of
 * farm-details), or the Düngejahr row for year-level routes
 * (`/.../fertilizer-years/:year`). The `none` level is used for routes that
 * are not part of the context hierarchy (e.g. `/home`, the welcome screen),
 * so that no sidebar row renders as active there.
 */
type Level = 'organization' | 'farm' | 'year' | 'none';

/**
 * Active entity identifiers parsed from the URL path.
 */
interface IActiveContextIds {
    organizationId: string | null;
    farmId: string | null;
    year: string | null;
}

/**
 * Active entity identifiers plus the sidebar context level.
 */
interface IActiveContext extends IActiveContextIds {
    level: Level;
}

/**
 * The farm fields the sidebar reads. Deliberately narrower than
 * `IFarmListItem` so the full farm entity (`IFarm`) satisfies it too: the
 * BETRIEB row resolves the open farm from its own entity when the switcher's
 * page does not contain it, and both sources have to feed the same row,
 * dropdown entry and `farmDetailPath`.
 */
type SidebarFarm = Pick<
    IFarmListItem,
    'active' | 'id' | 'managingOrganizationId' | 'nameWithLegalForm'
>;

const organizationsPathPrefix = '/organizations/';
const farmsPathPrefix = '/farms/';

/**
 * The switcher's slice of a farm page: the entries it offers, plus the size of
 * the set they were cut from. `total` decides the BETRIEB row's label, its
 * empty state, and whether the list points at the axis' farm list.
 */
interface IFarmSwitcherPage {
    items: Array<IFarmListItem>;
    total: number;
}

// How many farms the switcher offers before it stops listing and points at the
// axis' farm list, which is paginated. Must stay at or below the server's page
// ceiling: the query schema rejects a larger limit rather than clamping it.
const farmSwitcherLimit = 15;

// How long a pointer has to rest on a held-off rail before the reveal counts as
// wanted. The way off the toggle above it crosses the rail in a fraction of
// this, and a reader who stops there is asking for the menu.
const railDwellDelayMs = 1000;

/**
 * The entry whose label is followed by the pending-invitation count. Named so
 * the renderer can attach that one piece of extra chrome without the entry
 * descriptor growing a field only this surface understands.
 */
const invitationsEntryId = 'invitations';

/**
 * The account popover's navigation entries, in display order. The two
 * administration entries are bound to the administration atom and are absent
 * for everyone else.
 */
const accountMenuEntries: ReadonlyArray<INavigationEntry> = [
    {
        id: 'account',
        label: 'Mein Konto',
        path: '/account',
    },
    {
        id: 'settings',
        label: 'Einstellungen',
        path: '/settings',
    },
    {
        id: invitationsEntryId,
        label: 'Einladungen',
        path: '/invitations',
    },
    {
        id: 'administration',
        label: 'Verwaltung',
        path: '/administration',
        permission: 'administration:manage',
    },
    {
        id: 'api-docs',
        label: 'API-Dokumentation',
        path: '/api-docs',
        permission: 'administration:manage',
    },
];

const pathParts = {
    organizationIdIndex: 2,
    farmsSegmentIndex: 3,
    farmIdIndex: 4,
    farmSubSegmentIndex: 5,
    yearValueIndex: 6,
    standaloneFarmIdIndex: 2,
    standaloneFarmSubSegmentIndex: 3,
    standaloneYearValueIndex: 4,
};

/**
 * Extracts a URL segment value unless it is an empty string or equals the
 * literal path keyword "new" (used for create routes).
 * @param segment - The raw URL segment.
 * @returns The resource identifier, or null.
 */
function extractResourceId (segment: string | undefined): string | null {
    if (segment === undefined || segment.length === 0 || segment === 'new') {
        return null;
    }

    return segment;
}

/**
 * Extracts the year identifier from the farm-specific sub-path segments.
 * @param segments - The path segments.
 * @returns The year string, or null.
 */
function extractYear (segments: Array<string>): string | null {
    if (segments[pathParts.farmSubSegmentIndex] !== 'fertilizer-years') {
        return null;
    }

    const yearSegment = segments[pathParts.yearValueIndex] ?? '';

    return yearSegment.length > 0 ? yearSegment : null;
}

/**
 * Creates an empty sidebar context.
 * @returns The empty context.
 */
function createEmptyContext (): IActiveContextIds {
    return {
        organizationId: null,
        farmId: null,
        year: null,
    };
}

/**
 * Parses standalone farm routes like `/farms/:farmId`.
 * @param pathname - The current pathname.
 * @returns The parsed context, or null when the path does not match.
 */
function parseStandaloneFarmContext (pathname: string): IActiveContextIds | null {
    if (!pathname.startsWith(farmsPathPrefix)) {
        return null;
    }

    const segments = pathname.split('/');
    const farmId = extractResourceId(segments[pathParts.standaloneFarmIdIndex]);

    if (farmId === null) {
        return createEmptyContext();
    }

    const year = segments[pathParts.standaloneFarmSubSegmentIndex] === 'fertilizer-years' ?
        (segments[pathParts.standaloneYearValueIndex] ?? '') : '';

    return {
        organizationId: null,
        farmId,
        year: year.length > 0 ? year : null,
    };
}

/**
 * Parses organization-scoped routes like `/organizations/:id/farms/:farmId`.
 * @param pathname - The current pathname.
 * @returns The parsed context, or null when the path does not match.
 */
function parseOrganizationContext (pathname: string): IActiveContextIds | null {
    if (!pathname.startsWith(organizationsPathPrefix)) {
        return null;
    }

    const segments = pathname.split('/');
    const organizationId = extractResourceId(segments[pathParts.organizationIdIndex]);

    if (organizationId === null || segments[pathParts.farmsSegmentIndex] !== 'farms') {
        return {
            organizationId,
            farmId: null,
            year: null,
        };
    }

    const farmId = extractResourceId(segments[pathParts.farmIdIndex]);

    if (farmId === null) {
        return {
            organizationId,
            farmId: null,
            year: null,
        };
    }

    return {
        organizationId,
        farmId,
        year: extractYear(segments),
    };
}

/**
 * Whether the given path segments describe a concrete year route
 * (i.e. `.../fertilizer-years/<year>`).
 * @param parts - The non-empty path segments.
 * @returns Whether the path targets a specific year.
 */
function isYearLevel (parts: Array<string>): boolean {
    const yearIndex = parts.indexOf('fertilizer-years');

    return yearIndex !== -1 && yearIndex + 1 < parts.length && parts[yearIndex + 1] !== 'new';
}

/**
 * Whether the given path segments describe a farm-level route. Covers
 * both the bare `/farms` "Alle Betriebe" empty state and
 * `/organizations/:id/farms/<non-empty>[...]` routes.
 * @param parts - The non-empty path segments.
 * @returns Whether the path targets farm level.
 */
function isFarmLevel (parts: Array<string>): boolean {
    if (parts[0] === 'farms') {
        return true;
    }

    const farmsIndex = parts.indexOf('farms');

    return farmsIndex !== -1 && farmsIndex + 1 < parts.length;
}

/**
 * Classifies the current URL path into a sidebar context level. The
 * level determines which sidebar row renders in the "aktiv" state.
 * Unlike the identifier parsers, the level does not care whether a
 * concrete entity id is present — `/farms/new` and
 * `/organizations/:id/farms/new` both classify as farm-level. Paths
 * outside the context hierarchy (notably `/home`) classify as `none`,
 * leaving all sidebar rows inactive.
 * @param pathname - The current pathname.
 * @returns The current context level.
 */
function computeLevel (pathname: string): Level {
    const parts = pathname.split('/').filter(part => part.length > 0);

    if (isYearLevel(parts)) {
        return 'year';
    }

    if (isFarmLevel(parts)) {
        return 'farm';
    }

    if (parts[0] === 'organizations') {
        return 'organization';
    }

    return 'none';
}

/**
 * Parses the current URL to determine the active organization/farm/year
 * context plus its sidebar level classification.
 * @returns The parsed context.
 */
function parseActiveContext (): IActiveContext {
    const { pathname } = window.location;
    const ids = parseStandaloneFarmContext(pathname) ??
        parseOrganizationContext(pathname) ??
        createEmptyContext();

    return {
        ...ids,
        level: computeLevel(pathname),
    };
}

/**
 * Application frame that wraps all authenticated pages.
 * Provides a header with logo and user menu, a context-selector sidebar that
 * navigates through Organisation → Betrieb → Düngejahr and lists the work
 * areas of the selected year below it, and a main-content slot.
 *
 * One button in the header carries the navigation at every width: above the
 * mobile breakpoint it collapses the sidebar to an icon rail and back, a choice
 * `sidebar_preference.ts` keeps per device; below it, it opens the drawer.
 *
 * Both navigation surfaces (the account popover and the work areas) render
 * from `INavigationEntry` lists: the active marking comes from the URL and the
 * visibility from the declared permission, so neither needs per-entry state.
 *
 * **The main area publishes its paddings as `--fm-main-content-padding-top`,
 * `--fm-main-content-padding-inline` and `--fm-main-content-padding-bottom`**: a custom property
 * inherits through the slot, where the padding itself cannot be read. `<fm-list-page>` lifts its
 * sticky heading row by the top one, and `<fm-api-docs>` carries all three as its own padding
 * while it scrolls its own page.
 *
 * **A page carrying `data-scrolls-itself` takes the scrolling over**: the main area turns into a
 * padding-less flex column that clips instead of scrolling, reserves no scrollbar gutter and keeps
 * the zero minimum width it had as a scroll container (an unbreakable line of the vendor's would
 * otherwise widen it past the body), so the page grows to fill it and its own scrollbar sits where
 * the main area's would. The content focus goes with it: a page arriving while the main area holds
 * the focus takes it over and hands it back when it leaves with it, because the keys scroll the
 * focused element's nearest scrollable ancestor and never a descendant.
 * `pages/api_docs_scroller_styles.ts` is the one page doing this, and has why.
 */
@customElement('fm-application-frame')
export class FmApplicationFrame extends LitElement {

    static styles = css`
        :host {
            --sidebar-nav-width: var(--fm-sidebar-width);
            --sidebar-scroll: auto;
            
            display: flex;
            flex-direction: column;
            height: 100vh;
            overflow: hidden;
            color: var(--fm-color-text-normal);
        }

        .header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            height: var(--fm-header-height);
            min-height: var(--fm-header-height);
            background: var(--fm-color-surface);
            border-bottom: var(--wa-border-width-s) solid var(--fm-color-border);
            box-sizing: border-box;
            z-index: var(--fm-layer-header);
        }

        .header-left,
        .drawer-header {
            display: flex;
            align-items: center;
            min-width: 0;
            gap: 1rem;
        }

        .menu-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: calc(var(--fm-rail-width) - var(--fm-focus-ring-overflow));
            height: var(--fm-touch-target);
            margin-left: var(--fm-focus-ring-overflow);
            flex-shrink: 0;
            padding: 0;
            border: none;
            border-radius: var(--wa-border-radius-m);
            background: transparent;
            color: var(--fm-color-text-quiet);
            font-size: var(--wa-font-size-m);
            cursor: pointer;
        }

        .menu-icon:hover,
        .menu-icon:focus-visible {
            background: var(--fm-color-hover-surface);
        }

        .menu-icon:focus-visible {
            outline: var(--wa-focus-ring);
            outline-offset: var(--wa-focus-ring-offset);
        }

        .logo {
            width: 1.75rem;
            height: 1.75rem;
            flex-shrink: 0;
        }

        .home-link {
            display: flex;
            align-items: center;
            gap: var(--wa-space-s);
            color: inherit;
            text-decoration: none;
            border-radius: var(--wa-border-radius-m);
            padding: var(--wa-space-xs);
            min-width: 0;
        }

        .home-link:hover {
            background: var(--fm-color-hover-surface);
        }

        .home-link:focus-visible {
            outline: var(--wa-border-width-m) solid var(--fm-color-brand-loud);
            outline-offset: 0.125rem;
        }

        .title {
            font-size: var(--wa-font-size-m);
            font-weight: var(--wa-font-weight-bold);
            line-height: 1.4;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            min-width: 0;
            flex: 1 1 auto;
        }

        .header-right {
            display: flex;
            align-items: center;
        }

        .user-menu-trigger {
            display: flex;
            align-items: center;
            gap: 0.625rem;
            height: var(--fm-touch-target);
            margin-right: var(--wa-space-xs);
            padding: var(--wa-space-2xs);
            border-radius: var(--wa-border-radius-m);
            cursor: pointer;
            border: none;
            background: transparent;
            font-family: inherit;
            color: inherit;
        }

        .user-menu-trigger:hover,
        .user-menu-trigger:focus-visible {
            background: var(--fm-color-hover-surface);
        }

        .user-menu-trigger:focus-visible {
            outline: var(--wa-focus-ring);
            outline-offset: var(--wa-focus-ring-offset);
        }

        .avatar {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 1.75rem;
            height: 1.75rem;
            border-radius: 50%;
            background: var(--fm-color-brand-loud);
            color: var(--fm-color-text-on-brand);
            font-size: var(--wa-font-size-xs);
            font-weight: var(--wa-font-weight-bold);
            flex-shrink: 0;
            user-select: none;
        }

        .user-name {
            font-size: var(--wa-font-size-s);
            font-weight: var(--wa-font-weight-normal);
        }

        .user-chevron {
            font-size: var(--fm-icon-size-xs);
            color: var(--fm-color-text-quiet);
            flex-shrink: 0;
        }

        .dropdown-user-info {
            padding: var(--wa-space-xs) var(--wa-space-m);
        }

        .dropdown-user-name {
            font-size: var(--wa-font-size-s);
            font-weight: var(--wa-font-weight-bold);
            line-height: 1.21;
            color: var(--fm-color-text-normal);
        }

        .dropdown-user-email {
            font-size: var(--wa-font-size-xs);
            font-weight: var(--wa-font-weight-normal);
            line-height: 1.25;
            color: var(--fm-color-text-quiet);
            margin-top: var(--wa-space-3xs);
        }

        .user-menu-popover::part(body) {
            padding: var(--wa-space-xs);
            border-radius: var(--fm-border-radius-card);
            min-width: 16rem;
            box-shadow: var(--wa-shadow-m);
        }

        .user-menu-popover wa-divider {
            --spacing: var(--wa-space-2xs);
        }

        .dropdown-design-section {
            padding: 0.375rem var(--wa-space-m);
        }

        .dropdown-design-caption {
            font-size: var(--wa-font-size-2xs);
            font-weight: var(--wa-font-weight-bold);
            color: var(--fm-color-text-quiet);
            margin-bottom: var(--wa-space-xs);
        }

        .user-menu-nav {
            display: flex;
            flex-direction: column;
        }

        .user-menu-item {
            display: flex;
            align-items: center;
            gap: var(--wa-space-xs);
            width: 100%;
            box-sizing: border-box;
            padding: var(--wa-space-xs) var(--wa-space-m);
            border: none;
            background: transparent;
            border-radius: var(--wa-border-radius-m);
            font-family: inherit;
            font-size: var(--wa-font-size-s);
            line-height: 1.21;
            color: var(--fm-color-text-normal);
            text-align: left;
            cursor: pointer;
        }

        .user-menu-item:hover {
            background: var(--fm-color-hover-surface);
        }

        .user-menu-item:focus-visible {
            outline: var(--wa-focus-ring);
            outline-offset: calc(-1 * var(--wa-focus-ring-width));
        }

        .user-menu-item[aria-current='page'],
        .user-menu-item[aria-current='page']:hover {
            background-color: var(--fm-color-brand-background);
            color: var(--fm-color-brand-quiet);
        }

        .user-menu-item--danger {
            color: var(--fm-color-danger);
        }

        .body {
            display: flex;
            flex: 1;
            overflow: hidden;
        }

        .sidebar {
            position: relative;
            width: var(--fm-sidebar-width);
            overflow: visible;
            z-index: var(--fm-layer-nav-overlay);
            transition: width 150ms;
        }
        
        .sidebar-scroll {
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            overflow-y: var(--sidebar-scroll);
            overflow-x: hidden;
        }

        .sidebar-nav {
            display: flex;
            flex-direction: column;
            width: var(--sidebar-nav-width);
            min-height: 100%;
            background: var(--fm-color-surface);
            border-right: var(--wa-border-width-s) solid var(--fm-color-border);
            box-sizing: border-box;
            transition: width 150ms;
        }

        @media (prefers-reduced-motion: reduce) {
            .sidebar,
            .sidebar-nav {
                transition: none;
            }
        }

        .sidebar[data-collapsed] {
            --rail-chrome: hidden;
            --rail-row-width: 1.875rem;
            --rail-row-overflow: hidden;
            --sidebar-nav-width: var(--fm-rail-width);
            --sidebar-scroll: hidden;
            
            width: var(--fm-rail-width);
        }

        .sidebar[data-collapsed][data-overlay-pinned],
        .sidebar[data-collapsed]:not([data-overlay-suppressed]):is(:hover, :focus-within) {
            --rail-chrome: visible;
            --rail-row-width: 100%;
            --rail-row-overflow: visible;
            --sidebar-nav-width: var(--fm-sidebar-width);
            --sidebar-scroll: auto;
        }

        .sidebar[data-collapsed] :is(.section-label, .version) {
            visibility: var(--rail-chrome);
            overflow: var(--rail-row-overflow);
            white-space: nowrap;
        }

        .sidebar[data-collapsed] :is(.context-row, .area-row) {
            width: var(--rail-row-width);
            overflow: var(--rail-row-overflow);
        }

        .sidebar-section {
            display: flex;
            flex-direction: column;
            gap: var(--wa-space-2xs);
            padding: var(--wa-space-m) var(--wa-space-xs) var(--wa-space-s);
            border-top: var(--wa-border-width-s) solid var(--fm-color-border);
        }

        .sidebar-section:first-child {
            border-top: none;
        }

        .section-label {
            font-size: var(--wa-font-size-2xs);
            font-weight: var(--wa-font-weight-bold);
            color: var(--fm-color-text-quiet);
            text-transform: uppercase;
            padding: 0 var(--wa-space-xs);
            margin-bottom: var(--wa-space-3xs);
            user-select: none;
            letter-spacing: 0.025rem;
        }

        .section-label--with-info {
            display: flex;
            align-items: center;
            justify-content: space-between;
        }

        .context-row {
            display: grid;
            grid-template-columns: 0.875rem 1fr 0.875rem;
            column-gap: var(--wa-space-xs);
            align-items: center;
            width: 100%;
            padding: 0.375rem var(--wa-space-xs);
            border: none;
            background: transparent;
            border-radius: var(--wa-border-radius-m);
            font: inherit;
            font-size: var(--wa-font-size-s);
            font-weight: var(--wa-font-weight-semibold);
            color: var(--fm-color-text-normal);
            text-align: left;
            line-height: 1;
            cursor: pointer;
            box-sizing: border-box;
        }

        .context-row[data-active] {
            background: var(--fm-color-brand-background);
            color: var(--fm-color-brand-quiet);
        }

        .context-row[data-empty] {
            color: var(--fm-color-text-quiet);
        }

        .context-row[data-empty-soft] {
            color: var(--fm-color-text-quiet);
            font-weight: var(--wa-font-weight-normal);
        }

        .context-row:focus-visible {
            outline: var(--wa-focus-ring);
            outline-offset: var(--wa-focus-ring-offset);
        }

        .context-row:hover:not([data-active]),
        .context-row:focus-visible:not([data-active]) {
            background: var(--fm-color-hover-surface);
        }

        .context-row-name {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;

            line-height: normal;
        }

        .context-icon {
            font-size: var(--wa-font-size-s);
            justify-self: center;
            color: currentColor;
        }

        .context-row-chevron {
            font-size: var(--fm-icon-size-xs);
            justify-self: center;
            color: var(--fm-color-text-quiet);
        }

        .context-row[data-active] .context-icon,
        .context-row[data-active] .context-row-chevron {
            color: var(--fm-color-brand-quiet);
        }

        .area-list {
            display: flex;
            flex-direction: column;
            gap: var(--wa-space-2xs);
            list-style: none;
            margin: 0;
            padding: 0;
        }

        .area-row {
            display: grid;
            grid-template-columns: 0.875rem 1fr;
            column-gap: var(--wa-space-xs);
            align-items: center;
            width: 100%;
            padding: 0.375rem var(--wa-space-xs);
            border-radius: var(--wa-border-radius-m);
            font-size: var(--wa-font-size-s);
            line-height: 1;
            color: var(--fm-color-text-quiet);
            text-decoration: none;
            box-sizing: border-box;
        }

        .area-row[data-active] {
            background: var(--fm-color-brand-background);
            color: var(--fm-color-brand-quiet);
        }

        .area-row:focus-visible {
            outline: var(--wa-focus-ring);
            outline-offset: var(--wa-focus-ring-offset);
            position: relative;
            z-index: 1;
        }

        .area-row:hover:not([data-active]),
        .area-row:focus-visible:not([data-active]) {
            background: var(--fm-color-hover-surface);
        }

        .area-status {
            font-size: var(--wa-font-size-m);
            justify-self: center;
            color: currentColor;
        }

        .area-row-name {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;

            line-height: normal;
        }

        .context-dropdown {
            display: flex;
            flex-direction: column;
            gap: 0;
            margin-top: var(--wa-space-xs);
        }

        .context-dropdown-group {
            display: flex;
            flex-direction: column;
            gap: 0;
        }

        .context-dropdown-item {
            display: grid;
            grid-template-columns: 1rem 1fr;
            column-gap: var(--wa-space-xs);
            align-items: start;
            min-height: 2rem;
            padding: var(--wa-space-xs);
            border-radius: 0;
            border: none;
            background: var(--fm-color-surface);
            font-family: inherit;
            font-size: var(--wa-font-size-s);
            font-weight: var(--wa-font-weight-normal);
            line-height: 1.3;
            color: var(--fm-color-text-normal);
            cursor: pointer;
            text-align: left;
            width: 100%;
            box-sizing: border-box;
        }

        .context-dropdown-item:hover {
            background: var(--fm-color-hover-surface);
        }

        .context-dropdown-item:focus-visible {
            outline: var(--wa-focus-ring);
            outline-offset: calc(-1 * var(--wa-focus-ring-width));
        }

        .context-dropdown-item:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

        .context-dropdown-item:disabled:hover {
            background: var(--fm-color-surface);
        }

        .context-dropdown-item[data-inactive]:not([data-selected]) {
            opacity: 0.62;
        }

        .context-dropdown-item[data-selected] {
            background: var(--fm-color-brand-background);
            color: var(--fm-color-brand-quiet);
            font-weight: var(--wa-font-weight-semibold);
        }

        .context-dropdown-item-bullet {
            width: 0.5rem;
            height: 0.5rem;
            border-radius: 50%;
            background: var(--fm-color-text-quiet);
            justify-self: center;
            margin-top: var(--wa-space-2xs);
        }

        .context-dropdown-item[data-selected] .context-dropdown-item-bullet {
            background: var(--fm-color-brand-loud);
        }

        .context-dropdown-item-icon {
            font-size: var(--fm-icon-size-xs);
            color: currentColor;
            justify-self: center;
            margin-top: var(--wa-space-2xs);
        }

        .context-dropdown-caption {
            padding: 0.625rem var(--wa-space-xs) var(--wa-space-2xs);
            font-size: var(--wa-font-size-s);
            font-weight: var(--wa-font-weight-semibold);
            color: var(--fm-color-text-quiet);
        }

        .context-dropdown-item-label {
            min-width: 0;
            overflow-wrap: anywhere;
        }

        .context-dropdown-item-add,
        .context-dropdown-item-more {
            color: var(--fm-color-brand-quiet);
        }

        .context-dropdown-item-anchor {
            display: block;
        }

        .context-dropdown-item-add fm-icon,
        .context-dropdown-item-more fm-icon {
            font-size: var(--fm-icon-size-xs);
            color: var(--fm-color-brand-quiet);
            justify-self: center;
            margin-top: var(--wa-space-2xs);
        }

        .context-dropdown-empty {
            font-size: var(--wa-font-size-s);
            color: var(--fm-color-text-quiet);
            padding: 0.375rem var(--wa-space-xs);
        }

        .context-dropdown-loading {
            display: flex;
            justify-content: center;
            padding: var(--wa-space-xs);
            font-size: var(--wa-font-size-s);
            color: var(--fm-color-text-quiet);
        }

        .version {
            color: var(--fm-color-text-quiet);
            font-size: var(--wa-font-size-xs);
            user-select: none;
            margin-top: auto;
            padding: var(--wa-space-s) var(--wa-space-m);
        }

        .main-content {
            --fm-main-content-padding-top: var(--wa-space-xl);
            --fm-main-content-padding-inline: var(--wa-space-l);
            --fm-main-content-padding-bottom: var(--wa-space-xl);

            flex: 1;
            position: relative;
            overflow-y: auto;
            scrollbar-gutter: stable both-edges;
            background: var(--fm-color-surface-page);
            padding:
                var(--fm-main-content-padding-top)
                var(--fm-main-content-padding-inline)
                var(--fm-main-content-padding-bottom);
            box-sizing: border-box;
        }

        .main-content[data-page-scrolls] {
            display: flex;
            flex-direction: column;
            min-width: 0;
            padding: 0;
            overflow: clip;
        }

        .main-content:focus {
            outline: none;
        }

        wa-drawer {
            --size: fit-content;
        }

        wa-drawer::part(body) {
            padding: 0;
        }

        wa-drawer::part(header) {
            display: none;
        }

        .drawer-content {
            display: flex;
            flex-direction: column;
            height: 100%;
            padding: 0;
            box-sizing: border-box;
        }

        .drawer-header {
            height: var(--fm-header-height);
            min-height: var(--fm-header-height);
            border-bottom: var(--wa-border-width-s) solid var(--fm-color-border);
            box-sizing: border-box;
        }

        .drawer-content .sidebar-scroll {
            position: static;
        }

        @media ${unsafeCSS(mobileMediaQuery)} {
            .sidebar {
                display: none;
            }

            .user-name,
            .user-chevron {
                display: none;
            }

            .user-menu-trigger {
                min-width: var(--fm-touch-target);
                min-height: var(--fm-touch-target);
                padding: 0.375rem;
                justify-content: center;
            }

            .context-row,
            .area-row {
                min-height: var(--fm-touch-target);
            }

            .area-list {
                gap: 0;
            }

            .main-content {
                --fm-main-content-padding-top: var(--wa-space-l);
                --fm-main-content-padding-inline: var(--wa-space-m);
                --fm-main-content-padding-bottom: var(--wa-space-l);
            }
        }
    `;

    private readonly displaySettings = new DisplaySettingsController(this);

    /*
     * Read once per element rather than through a cache in the preference
     * library, so a first render already has the stored width and nothing
     * flashes open before collapsing.
     */
    @state()
    private sidebarCollapsed = readSidebarCollapsed();

    /*
     * The viewport tier the menu button branches on, projected into state from
     * the frame's own media query (`boundMobileMediaQueryChangeHandler`)
     * because the button's accessible name differs by width and a render may
     * not ask the viewport directly.
     */
    @state()
    private mobileTier = false;

    @state()
    private pageScrollsItself = false;

    @state()
    private railOverlaySuppressed = false;

    @state()
    private userInfo: IUserInfo | null = null;

    @state()
    private activeContext: IActiveContext = parseActiveContext();

    /*
     * Pathname tracked separately from `activeContext` because the dropdown's
     * current-page highlight needs to distinguish leaf routes (`/account`,
     * `/administration`) that all resolve to `level: 'none'` in `parseActiveContext`.
     * Updated in the popstate handler, which fires on browser back/forward and on
     * SPA navigation alike: `redirectTo` (navigation.ts) dispatches a synthetic
     * popstate after `pushState`.
     */
    @state()
    private currentPathname: string = window.location.pathname;

    @state()
    private organizations: Array<IOrganizationListItem> | null = null;

    @state()
    private years: Array<IFertilizerYear> | null = null;

    @state()
    private orgDropdownOpen = false;

    @state()
    private farmDropdownOpen = false;

    @state()
    private yearDropdownOpen = false;

    @state()
    private drawerOpen = false;

    @state()
    private userMenuOpen = false;

    private loadedYearsForFarmId: string | null = null;

    private organizationsLoadToken = 0;

    private yearsLoadToken = 0;

    private permissionsLoadToken = 0;

    /**
     * The administration atoms the caller holds, the set every entry's
     * `permission` is resolved against. Empty until the snapshot has been
     * read, and empty again whenever it cannot be read, so a bound entry is
     * never offered on an unresolved snapshot.
     */
    @state()
    private heldPermissions: ReadonlyArray<AdministrationRole> = [];

    @state()
    private pendingInvitationCount: number = 0;

    /**
     * The open farm as its own entity, so the BETRIEB row, the dropdown entry
     * and the Düngejahr CTA survive a switcher slice that does not reach the
     * farm. `args` keys the run on the farm id alone, which leaves tab and
     * year navigation within one farm on the cached value.
     *
     * Failures resolve to `null` rather than an ERROR status: every consumer
     * reads the value through `activeFarm`, whose fallbacks already cover a
     * farm that cannot be resolved.
     */
    private readonly activeFarmTask = new Task(this, {
        task: async ([
            farmId,
        ], { signal }): Promise<SidebarFarm | null> => {
            if (farmId === null) {
                return null;
            }

            const result = await getFarmById(farmId, {
                signal,
            });

            return result.success ? result.value : null;
        },
        args: (): [string | null] => [
            this.activeContext.farmId,
        ],
    });

    /**
     * Whether the caller may commit a farm to the active organisation
     * (`organization:write`), mirroring `callerCanAttachOrganization`
     * (`server/services/farm/farm_service.ts`), which is what the switcher's
     * "Betrieb anlegen" entry needs: under an organisation context it leads to the
     * org-scoped route, where the farm is assigned to that organisation.
     *
     * Keyed on the active organisation and re-run on the permissions signals, like
     * {@link activeFarmTask}. Unresolved reads as `undefined`, so the entry is absent
     * until the snapshot answers rather than offered on an unknown one.
     */
    private readonly organizationWriteTask = new Task(this, {
        task: async ([
            organizationId,
        ]): Promise<boolean> => organizationId !== null &&
            await hasOrganizationAccess(organizationId, 'organization:write'),
        args: (): [string | null] => [
            this.activeContext.organizationId,
        ],
    });

    /**
     * The BETRIEB switcher's farms for the active scope.
     *
     * Always the caller-scoped list (`GET /api/v1/farms`), never the org
     * roster: the roster answers a different question (a management view that
     * includes farms the caller cannot open) and it is gated on
     * `organization:read`, which a farm-only member does not hold. The
     * `organizationId` filter narrows the list to the active scope's axis
     * server-side, so the switcher's slice is spent exclusively on farms that
     * can actually be opened.
     *
     * The inactive filter is applied server-side so the slice is filled with
     * entries the user can see; filtering afterwards would drop entries the
     * slice already paid for and hide active farms behind the cut. The open
     * farm does not depend on this slice: `activeFarmTask` resolves it from its
     * own entity, and `getFarmDropdownEntries` leads the list with it.
     *
     * Failures resolve to an empty set rather than an ERROR status, which the
     * row label and the empty state read as "no farms".
     */
    private readonly farmsTask = new Task(this, {
        task: async ([
            organizationId,
            hideInactive,
        ], { signal }): Promise<IFarmSwitcherPage> => {
            const result = await listAccessibleFarms({
                offset: 0,
                limit: farmSwitcherLimit,
                hideInactive,
                ...(organizationId === null ? {} : {
                    organizationId,
                }),
            }, {
                signal,
            });

            return result.success ? result.value : {
                items: [],
                total: 0,
            };
        },
        args: (): [string | null, boolean] => [
            this.activeContext.organizationId,
            this.displaySettings.hideInactive,
        ],
    });

    /**
     * The work-area states of the open Düngejahr: one request per year, and only inside
     * one.
     *
     * Resolves to `null`, never to an empty list, while either id is unknown. An empty
     * answer is a real answer — the server judged every area and named none — and rendering
     * it would put „Keine Daten“ on every Bereich of a year whose id has simply not
     * been resolved yet. That happens on every deep link, the year list arriving after the
     * first paint, and permanently whenever that list cannot be read at all.
     *
     * A failure is likewise left to **throw** rather than resolved to a fallback, because
     * there is no honest fallback here: the empty glyph means „Keine Daten“, so
     * rendering it on a failed read would claim a state nobody measured. The pending, error
     * and unknown-id arms therefore coincide, and all three draw no glyph at all (see
     * {@link renderAreaEntry}).
     *
     * Keyed on the two ids rather than on `years`, whose array identity changes on every
     * reload and would re-run the read for nothing.
     */
    private readonly areaStatesTask = new Task(this, {
        task: async ([
            farmId,
            fertilizerYearId,
        ], { signal }): Promise<Array<IFertilizerYearAreaState> | null> => {
            if (farmId === null || fertilizerYearId === null) {
                return null;
            }

            return valueOrThrow(await listFertilizerYearAreaStates(farmId, fertilizerYearId, {
                signal,
            }));
        },
        args: (): [string | null, string | null] => [
            this.activeContext.farmId,
            this.openFertilizerYearId,
        ],
    });

    /**
     * Re-reads the work-area states after a write below the open Düngejahr. Filtered on
     * both ids, so a write to another year the user has open in a second tab does not
     * refetch here.
     * @param event - The data-change event.
     */
    private readonly boundFertilizerYearDataChangedHandler = (
        event: WindowEventMap['fm-fertilizer-year-data-changed']
    ): void => {
        const { farmId, fertilizerYearId } = event.detail;

        if (farmId === this.activeContext.farmId && fertilizerYearId === this.openFertilizerYearId) {
            void this.areaStatesTask.run();
        }
    };

    private readonly boundPopstateHandler = (): void => {
        this.activeContext = parseActiveContext();
        this.currentPathname = window.location.pathname;
        this.closeAllDropdowns();
        void this.loadYearList(this.activeContext.farmId);
    };

    // Re-resolve the held administration atoms whenever the permissions
    // snapshot is invalidated (navigation, data-change event, or cross-tab
    // signal) so the entries bound to them appear or disappear without a
    // full page reload after a promotion or demotion.
    private readonly boundPermissionsChangedHandler = (): void => {
        void this.resolvePermissions();
        void this.organizationWriteTask.run();
    };

    /**
     * Refetches the sidebar's fertilizer-year list when the page-level
     * `<fm-farm-detail>` reports a successful create. Page-internal mutations
     * keep the URL stable, so the popstate-based invalidation path never
     * fires; this listener provides the missing notification edge.
     * @param event - The dispatched custom event with the affected `farmId`.
     */
    private readonly boundFertilizerYearsChangedHandler = (
        event: WindowEventMap['fm-fertilizer-years-changed']
    ): void => {
        if (event.detail.farmId !== this.activeContext.farmId) {
            return;
        }

        this.loadedYearsForFarmId = null;
        void this.loadYearList(this.activeContext.farmId);
    };

    /**
     * Refetches the sidebar's farm list when the page-level
     * `<fm-farm-detail>` reports a successful mutation (status toggle,
     * rename, address edit). The cached `IFarmListItem` carries `name`
     * and `active`, both of which the sidebar BETRIEB row and the
     * Düngejahr CTA read; without this listener the cache stays stale
     * until the next navigation. Skips events for farms the frame
     * doesn't currently have cached, except the open one: it may sit
     * outside the switcher's slice and is then held by `activeFarmTask`
     * alone.
     * @param event - The dispatched custom event with the affected `farmId`.
     */
    private readonly boundFarmChangedHandler = (
        event: WindowEventMap['fm-farm-changed']
    ): void => {
        const { farmId } = event.detail;
        const isOpenFarm = farmId === this.activeContext.farmId;

        if (this.farmPage?.items.some(farm => farm.id === farmId) !== true && !isOpenFarm) {
            return;
        }

        if (isOpenFarm) {
            void this.activeFarmTask.run();
        }

        void this.farmsTask.run();
    };

    /**
     * Refetches the sidebar's organization list whenever a page-level
     * component reports a change to the user's organisation list — an
     * org was added (`<fm-organization-create>` after a successful
     * create), renamed, or had its address edited
     * (`<fm-organization-detail>` after a successful save-edit).
     * `IOrganizationListItem.name` drives the sidebar ORGANISATION row
     * label. The refetch is unconditional because the user-level
     * organisation list has no scope key — `loadOrganizations` always
     * fetches the whole list.
     */
    private readonly boundOrganizationChangedHandler = (): void => {
        void this.loadOrganizations();
    };

    /**
     * Refetches the sidebar's farm list when a page-level component
     * reports a wholesale change to the active scope's farm list (e.g.
     * a transfer confirm that adds a new farm to the current
     * organisation). Unlike `fm-farm-changed`, this event does not
     * require the farm to already be in the sidebar cache — its purpose
     * is precisely to discover farms the sidebar has not yet seen.
     * Skips events for scopes other than the one the sidebar is
     * currently displaying; the switcher holds a single scope at a time,
     * so other scopes will refetch on the next navigation.
     * @param event - The dispatched custom event with the affected scope.
     */
    private readonly boundFarmsChangedHandler = (
        event: WindowEventMap['fm-farms-changed']
    ): void => {
        if (event.detail.organizationId !== this.activeContext.organizationId) {
            return;
        }

        void this.farmsTask.run();
    };

    /**
     * Drops every access-derived sidebar cache when the user's own grant
     * set changes (accepting an invitation can add an organisation and
     * farms in one step). Unlike `fm-farms-changed` this carries no
     * scope and is deliberately not filtered: the dispatcher cannot know
     * which scope the sidebar is displaying, and the organisation list
     * has no scope key at all. Both tasks are keyed on ids that a grant
     * change leaves untouched, so they need the explicit re-run to notice
     * access they have lost.
     */
    private readonly boundAccessChangedHandler = (): void => {
        this.loadedYearsForFarmId = null;
        void this.activeFarmTask.run();
        void this.organizationWriteTask.run();
        void this.farmsTask.run();
        void this.loadOrganizations();
        void this.loadYearList(this.activeContext.farmId);
    };

    /**
     * Covers the cross-tab half of the same signal. `fm-access-changed`
     * never leaves its tab, so a grant accepted next door would otherwise
     * leave this sidebar stale for the rest of the session — the very
     * symptom the in-tab listener fixes. `resetPermissionsCache()` writes
     * the bust key, and `permissions.ts` registers its own `storage`
     * listener at module evaluation, so the snapshot is already dropped
     * by the time this runs.
     * @param event - The cross-tab storage event.
     */
    private readonly boundPermissionsBustStorageHandler = (event: StorageEvent): void => {
        if (event.key !== permissionsCacheBustKey) {
            return;
        }

        this.boundAccessChangedHandler();
    };

    /**
     * Refreshes the pending-invitations count whenever the shared
     * invitations store reports a change. Drives the counter badge
     * inside the "Einladungen" dropdown entry and the red trigger
     * badge on the avatar so they stay in sync with the dashboard
     * banner.
     */
    private readonly boundInvitationsChangedHandler = (): void => {
        void this.refreshPendingInvitationCount();
    };

    /**
     * Whether the revealed rail is the surface the reader is on, so Escape
     * dismisses no rail nobody is pointing at or focused in.
     * @returns Whether the pointer or the focus is inside the sidebar.
     */
    private isRailEngaged (): boolean {
        const root = this.shadowRoot;

        if (root === null) {
            return false;
        }

        const sidebar = root.querySelector('.sidebar');

        if (sidebar === null) {
            return false;
        }

        return sidebar.matches(':hover') || sidebar.contains(root.activeElement);
    }

    /**
     * Dismisses the revealed rail on Escape, which WCAG 2.2 SC 1.4.13 asks of
     * content shown on hover or focus and CSS has nowhere to keep. The listener
     * is on `window` because a pointer-only reader's key press never reaches
     * this shadow root.
     * @param event - The key event.
     */
    private readonly boundRailEscapeHandler = (event: KeyboardEvent): void => {
        if (event.key !== 'Escape' || !this.sidebarCollapsed) {
            return;
        }

        if (!this.isRailEngaged()) {
            return;
        }

        this.suppressRailOverlay();

        // An open dropdown holds the reveal, so dismissing without closing it
        // would leave a 256-point list rendering inside a 46-point rail.
        this.closeAllDropdowns();
    };

    /**
     * One signal for every release listener of a held-off reveal, so the first
     * to fire drops the rest; `once` would leave the ones that did not fire
     * watching the header and the content for the rest of the session.
     */
    private railRelease: AbortController | null = null;

    /**
     * Holds the reveal off until the pointer says it is elsewhere, and arms
     * every way that can happen. Leaving the page is this element's own
     * `pointerleave`, its box being the viewport, bound here rather than in the
     * template because Lit has no host binding and a leave event does not
     * bubble, so `onWindowEvent` would never see it. `pointerenter` rather than
     * `pointerover`, so an arrival counts once instead of at every element
     * boundary inside the page.
     */
    private suppressRailOverlay (): void {
        this.railOverlaySuppressed = true;
        this.railRelease?.abort();

        const release = new AbortController();
        const { signal } = release;
        const sidebar = this.renderRoot.querySelector('.sidebar');

        this.railRelease = release;
        this.addEventListener('pointerleave', this.handleRailReturn, {
            signal,
        });
        this.renderRoot.querySelector('.header .home-link')?.addEventListener('pointerenter', this.handleRailReturn, {
            signal,
        });
        this.renderRoot.querySelector('.main-content')?.addEventListener('pointerenter', this.handleRailReturn, {
            signal,
        });
        sidebar?.addEventListener('pointerenter', this.startRailDwell, {
            signal,
        });
    }

    /**
     * Starts the wait an arriving pointer sits out before the rail may open
     * after all. Nothing cancels it on the way out, the `:hover` rule asking by
     * itself whether the pointer is still there. What it may not do is end a
     * *later* hold, so it acts only while the suppression it was armed under is
     * still in force.
     */
    private readonly startRailDwell = (): void => {
        const armed = this.railRelease;

        window.setTimeout(() => {
            if (this.railRelease === armed) {
                this.handleRailReturn();
            }
        }, railDwellDelayMs);
    };

    private readonly handleRailReturn = (): void => {
        this.railOverlaySuppressed = false;
        this.railRelease?.abort();
        this.railRelease = null;
    };

    private mobileMediaQueryList: MediaQueryList | null = null;

    /**
     * Closes the mobile drawer and any open context dropdowns when the
     * viewport widens past the mobile breakpoint. Without this the drawer
     * `<wa-drawer>` element stays visually open after the desktop sidebar
     * has reappeared, since the responsive CSS only toggles the desktop
     * `.sidebar`'s `display` and never touches `drawerOpen`.
     *
     * It also carries the tier the menu button branches on. The collapse
     * preference is left alone in both directions, describing the wide layout
     * only, so a trip through the narrow one gives it back unchanged.
     * @param event - The media-query change event.
     */
    private readonly boundMobileMediaQueryChangeHandler = (
        event: MediaQueryListEvent
    ): void => {
        this.mobileTier = event.matches;

        if (event.matches) {
            return;
        }

        if (this.drawerOpen) {
            this.drawerOpen = false;
        }

        this.closeAllDropdowns();
    };

    /**
     * Loads the list of organizations the user belongs to.
     */
    private async loadOrganizations (): Promise<void> {
        const token = ++this.organizationsLoadToken;
        const result = await listOrganizations();

        if (token !== this.organizationsLoadToken) {
            return;
        }

        if (result.success) {
            this.organizations = result.value;
        } else {
            this.organizations = [];
        }
    }

    private async loadYearList (farmId: string | null): Promise<void> {
        if (farmId === null) {
            this.years = null;
            this.loadedYearsForFarmId = null;

            return;
        }

        if (farmId === this.loadedYearsForFarmId) {
            return;
        }

        const token = ++this.yearsLoadToken;
        this.loadedYearsForFarmId = farmId;
        this.years = null;

        const result = await listFertilizerYearsByFarmId(farmId);

        if (token !== this.yearsLoadToken) {
            return;
        }

        if (result.success) {
            this.years = result.value;

            return;
        }

        this.loadedYearsForFarmId = null;
        this.years = [];
    }

    private readonly windowEvents = new WindowEventsController(this, [
        onWindowEvent('popstate', this.boundPopstateHandler),
        onWindowEvent('fm-fertilizer-years-changed', this.boundFertilizerYearsChangedHandler),
        onWindowEvent('fm-farm-changed', this.boundFarmChangedHandler),
        onWindowEvent('fm-organization-changed', this.boundOrganizationChangedHandler),
        onWindowEvent('fm-farms-changed', this.boundFarmsChangedHandler),
        onWindowEvent('fm-access-changed', this.boundAccessChangedHandler),
        onWindowEvent('storage', this.boundPermissionsBustStorageHandler),
        onWindowEvent('keydown', this.boundRailEscapeHandler),
        onWindowEvent('fm-invitations-changed', this.boundInvitationsChangedHandler),
        onWindowEvent('fm-fertilizer-year-data-changed', this.boundFertilizerYearDataChangedHandler),
        onWindowEvent(permissionsChangedEventName, this.boundPermissionsChangedHandler),
    ]);

    override connectedCallback (): void {
        super.connectedCallback();
        this.userInfo = getUserInfo();
        this.mobileMediaQueryList = window.matchMedia(mobileMediaQuery);
        this.mobileTier = this.mobileMediaQueryList.matches;
        this.mobileMediaQueryList.addEventListener(
            'change',
            this.boundMobileMediaQueryChangeHandler
        );
        void hydrateDisplaySettingsOnce();
        void this.resolvePermissions();
        void this.refreshPendingInvitationCount();
        void this.loadOrganizations();
        void this.loadYearList(this.activeContext.farmId);
    }

    /**
     * Reads the latest pending-invitations count from the shared
     * store, whose rejection on a transient failure is swallowed here:
     * the badge is ambient chrome, so reporting the failure would give
     * the user nothing to act on, and the store caches no failure, so
     * the next read retries by itself.
     */
    private async refreshPendingInvitationCount (): Promise<void> {
        try {
            this.pendingInvitationCount = await getPendingInvitationCount();
        } catch {
            this.pendingInvitationCount = 0;
        }
    }

    /**
     * Reads the caller's administration atoms from the shared permissions
     * cache, so the snapshot is fetched at most once per session (shared with
     * the route guard in `app.ts`).
     *
     * Token-guarded like the other loaders here: invalidations arrive on every
     * navigation and from sibling tabs, so two resolutions can overlap, and an
     * older one settling last would leave a demoted caller with the entries its
     * atoms no longer grant.
     */
    private async resolvePermissions (): Promise<void> {
        const token = ++this.permissionsLoadToken;
        const snapshot = await getPermissions();

        if (token !== this.permissionsLoadToken) {
            return;
        }

        this.heldPermissions = snapshot?.administration ?? [];
    }

    override disconnectedCallback (): void {
        super.disconnectedCallback();
        this.mobileMediaQueryList?.removeEventListener(
            'change',
            this.boundMobileMediaQueryChangeHandler
        );
        this.mobileMediaQueryList = null;
        this.railRelease?.abort();
        this.railRelease = null;
    }

    /**
     * Names the two overlays this frame draws, neither of which WebAwesome names
     * itself (`docs/design/foundations.md` → *Accessibility → Recurring patterns*).
     */
    override updated (): void {
        void nameDialog(this.renderRoot.querySelector('wa-drawer'), 'Navigation');
        void nameDialog(this.renderRoot.querySelector('wa-popover'), 'Benutzermenü');
    }

    private closeAllDropdowns (): void {
        this.orgDropdownOpen = false;
        this.farmDropdownOpen = false;
        this.yearDropdownOpen = false;
    }

    /**
     * Whether an open context dropdown holds the revealed rail open: a pointer
     * leaving the rail would otherwise snap a 256-point dropdown back into
     * 46 points. Safari does not focus a clicked button, so `:focus-within`
     * alone does not cover it.
     * @returns Whether any context dropdown is open.
     */
    private get anyDropdownOpen (): boolean {
        return this.orgDropdownOpen || this.farmDropdownOpen || this.yearDropdownOpen;
    }

    /** Whether a navigation closed the drawer and still owes focus to the content. */
    private contentFocusPending = false;

    /**
     * Puts the navigation away and hands focus to the content.
     *
     * Focus left on the clicked row holds the revealed rail open through
     * `:focus-within`.
     */
    private dismissNavigation (): void {
        const throughDrawer = this.drawerOpen;

        this.closeAllDropdowns();
        this.drawerOpen = false;

        if (throughDrawer) {
            this.contentFocusPending = true;

            return;
        }

        void this.focusContent();
    }

    /** Moves focus to the content region, once the pending render has been applied. */
    private async focusContent (): Promise<void> {
        await this.updateComplete;
        this.mainContent()?.focus();
    }

    private mainContent (): HTMLElement | null {
        return this.renderRoot.querySelector<HTMLElement>('.main-content');
    }

    /**
     * Takes the focus back from a drawer that has finished hiding.
     *
     * `<wa-drawer>` queues its own restore to the opening control before it announces
     * the hide, so this one is queued behind it.
     */
    private releaseContentFocus (): void {
        if (!this.contentFocusPending) {
            return;
        }

        this.contentFocusPending = false;
        window.setTimeout(() => {
            void this.focusContent();
        });
    }

    private navigate (path: string): void {
        this.dismissNavigation();
        redirectTo(path);
    }

    private readonly handleHomeLinkClick = (event: MouseEvent): void => {
        if (!isPlainFollow(event)) {
            return;
        }

        event.preventDefault();

        if (window.location.pathname === '/home') {
            this.dismissNavigation();

            return;
        }

        this.navigate('/home');
    };

    private getInitials (): string {
        if (this.userInfo === null) {
            return '';
        }

        const first = this.userInfo.givenName.charAt(0);
        const last = this.userInfo.familyName.charAt(0);

        return `${first}${last}`.toUpperCase();
    }

    /**
     * Asks about unsaved changes first, while the session can still save them, and only then
     * ends it: a question asked after the logout could offer nothing but a refused save.
     */
    private readonly handleLogout = (): void => {
        void (async (): Promise<void> => {
            if (!await confirmLeaving()) {
                return;
            }

            resetSessionCaches();
            await logout();
            redirectAway('/login');
        })();
    };

    private readonly handleUserMenuShow = (event: Event): void => {
        if (event.target !== event.currentTarget) {
            return;
        }

        this.userMenuOpen = true;
    };

    private readonly handleUserMenuAfterHide = (event: Event): void => {
        if (event.target !== event.currentTarget) {
            return;
        }

        this.userMenuOpen = false;
    };

    // The account popover focuses the colour-scheme switcher first so the
    // panel's first stop is the DESIGN section, matching the visual order.
    private readonly handleUserMenuAfterShow = (event: Event): void => {
        if (event.target !== event.currentTarget) {
            return;
        }

        this.getUserMenuSwitcher()?.focusSelected();
    };

    private getUserMenuSwitcher (): FmColorSchemeSwitcher | null {
        return this.renderRoot.querySelector<FmColorSchemeSwitcher>('fm-color-scheme-switcher');
    }

    /**
     * The roving stops of the account popover: the colour-scheme switcher
     * (a single stop, navigated internally by Left/Right) followed by the
     * visible nav buttons. The switcher is omitted when not yet rendered.
     * @returns The ordered focus stops.
     */
    private getUserMenuStops (): Array<HTMLElement> {
        const switcher = this.getUserMenuSwitcher();
        const items = Array.from(this.renderRoot.querySelectorAll<HTMLElement>('.user-menu-item'));

        return switcher === null ? items : [
            switcher,
            ...items,
        ];
    }

    private focusUserMenuStop (stop: HTMLElement): void {
        const switcher = this.getUserMenuSwitcher();

        if (switcher !== null && stop === switcher) {
            switcher.focusSelected();

            return;
        }

        stop.focus();
    }

    // Capture phase so Up/Down/Home/End are claimed before the switcher's
    // inner wa-radio-group sees them; otherwise navigating onto or past the
    // switcher would change the colour scheme as a side effect. Left/Right
    // are left untouched and reach the radio-group for deliberate selection.
    private readonly userMenuKeydownListener = {
        handleEvent: (event: Event): void => {
            if (event instanceof KeyboardEvent) {
                this.onUserMenuKeydown(event);
            }
        },
        capture: true,
    };

    private onUserMenuKeydown (event: KeyboardEvent): void {
        if (![
            'ArrowDown',
            'ArrowUp',
            'Home',
            'End',
        ].includes(event.key)) {
            return;
        }

        const stops = this.getUserMenuStops();

        if (stops.length === 0) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.focusUserMenuStop(stops[this.nextUserMenuStopIndex(event, stops)]);
    }

    private nextUserMenuStopIndex (event: KeyboardEvent, stops: Array<HTMLElement>): number {
        const lastIndex = stops.length - 1;

        if (event.key === 'Home') {
            return 0;
        }

        if (event.key === 'End') {
            return lastIndex;
        }

        const step = event.key === 'ArrowDown' ? 1 : -1;
        const path = event.composedPath();
        const current = stops.findIndex(stop => path.includes(stop));

        if (current === -1) {
            return event.key === 'ArrowDown' ? 0 : lastIndex;
        }

        return (current + step + stops.length) % stops.length;
    }

    private readonly handleNavClick = (path: string): void => {
        this.drawerOpen = false;
        redirectTo(path);
    };

    /**
     * The entries of a navigation surface the caller may be offered.
     * @param entries - The surface's declared entries.
     * @returns The visible subset, in declaration order.
     */
    private visibleEntries (entries: ReadonlyArray<INavigationEntry>): Array<INavigationEntry> {
        return entries.filter(entry => isNavigationEntryVisible(entry, this.heldPermissions));
    }

    private readonly handleOpenDrawer = (): void => {
        this.drawerOpen = true;
    };

    /**
     * Serves the one menu button at every width: below the breakpoint the
     * navigation is a drawer to be opened, above it a sidebar to be collapsed.
     */
    private readonly handleMenuButtonClick = (): void => {
        if (this.mobileTier) {
            this.handleOpenDrawer();

            return;
        }

        this.sidebarCollapsed = !this.sidebarCollapsed;
        writeSidebarCollapsed(this.sidebarCollapsed);
        this.closeAllDropdowns();

        // The button stands directly on the rail, so the pointer's way off it
        // lands in the reveal and reopens the menu the press just closed.
        // Expanding gives the reveal back, so the next collapse starts clean.
        if (this.sidebarCollapsed) {
            this.suppressRailOverlay();

            return;
        }

        this.handleRailReturn();
    };

    /**
     * Names the menu button after what pressing it does, which the glyph says too
     * ({@link FmApplicationFrame.menuIcon}) rather than the name carrying the state on its
     * own (`docs/design/pages.md` → *Mobile Patterns*). The tooltip
     * shows this same text: `<wa-tooltip for>` moves the button's accessible name
     * onto its own `aria-labelledby`, so the two cannot be worded apart.
     * @returns The button's accessible name.
     */
    private get menuButtonLabel (): string {
        if (this.mobileTier) {
            return 'Navigation öffnen';
        }

        return this.sidebarCollapsed ? 'Navigation ausklappen' : 'Navigation einklappen';
    }

    private readonly handleDrawerHide = (): void => {
        this.drawerOpen = false;
    };

    // ─── Organization context ───────────────────────────────────────

    private get activeOrganization (): IOrganizationListItem | null {
        const { organizationId } = this.activeContext;

        if (organizationId === null || this.organizations === null) {
            return null;
        }

        return this.organizations.find(o => o.id === organizationId) ?? null;
    }

    private renderOrganizationRow (): TemplateResult {
        const active = this.activeOrganization;
        const name = active !== null ? active.nameWithLegalForm : 'Alle';
        const isActive = this.activeContext.level === 'organization';
        const rowIcon = active !== null ? organizationTypeIcon(active.type) : faListUl;

        return html`
            <button class="context-row"
                    ?data-active=${isActive}
                    aria-label=${`Organisation: ${name}`}
                    aria-controls=${this.orgDropdownOpen ? 'context-dropdown-organization' : nothing}
                    aria-expanded=${this.orgDropdownOpen ? 'true' : 'false'}
                    @click=${this.handleOrgRowClick}>
                <fm-icon class="context-icon" .icon=${rowIcon} auto-width></fm-icon>
                <span class="context-row-name">${name}</span>
                <fm-icon class="context-row-chevron"
                         .icon=${this.orgDropdownOpen ? faAngleUp : faAngleDown}
                         auto-width></fm-icon>
            </button>
            ${this.orgDropdownOpen ? this.renderOrganizationDropdown() : nothing}
        `;
    }

    private readonly handleOrgRowClick = (): void => {

        // Empty org list: the dropdown would only contain "Alle" (already
        // selected) and "+ Organisation anlegen". Navigate to /organizations
        // so Screen 01's empty state with its primary CTA is surfaced in the
        // main content area instead of a near-empty popover.
        if (this.organizations !== null && this.organizations.length === 0) {
            this.navigate('/organizations');

            return;
        }

        this.orgDropdownOpen = !this.orgDropdownOpen;
    };

    private renderOrganizationDropdown (): TemplateResult {
        if (this.organizations === null) {
            return html`<div class="context-dropdown" id="context-dropdown-organization">
                <div class="context-dropdown-loading">
                    <wa-spinner></wa-spinner>
                </div>
            </div>`;
        }

        const { organizationId } = this.activeContext;
        const visible = filterInactive(this.organizations, this.displaySettings.hideInactive, organizationId);
        const consulting = visible.filter(org => org.type === 'consulting');
        const managing = visible.filter(org => org.type === 'management');

        return html`
            <div class="context-dropdown" id="context-dropdown-organization">
                <button class="context-dropdown-item"
                        ?data-selected=${organizationId === null}
                        aria-current=${organizationId === null ? 'true' : nothing}
                        @click=${(): void => this.navigate('/organizations')}>
                    <fm-icon class="context-dropdown-item-icon" .icon=${faListUl} auto-width></fm-icon>
                    <span class="context-dropdown-item-label">Alle</span>
                </button>
                ${consulting.length > 0 ?
                    html`
                        ${this.renderOrgGroup('Beratende Organisationen', 'consulting', consulting, organizationId)}
                        ${managing.length > 0 ?
                            this.renderOrgGroup('Verwaltende Organisationen', 'management', managing, organizationId) :
                            nothing}
                    ` :
                    visible.map(org => this.renderOrgDropdownItem(org, organizationId))}
                <button class="context-dropdown-item context-dropdown-item-add"
                        @click=${(): void => this.navigate('/organizations/new')}>
                    <fm-icon .icon=${faPlus} auto-width></fm-icon>
                    <span class="context-dropdown-item-label">Organisation anlegen</span>
                </button>
            </div>
        `;
    }

    /**
     * Renders a type-grouped section of the organisation dropdown: a caption
     * header followed by the organisations of that type. Used only when the
     * user belongs to at least one consulting organisation; managing-only
     * users see a flat, uncaptioned list.
     * @param label - The caption shown above the group.
     * @param groupId - Stable token used to associate the group with its caption.
     * @param organizations - The organisations of this type.
     * @param selectedId - The active organisation id, or null for "Alle".
     * @returns The labelled group of the type's items.
     */
    private renderOrgGroup (
        label: string,
        groupId: string,
        organizations: Array<IOrganizationListItem>,
        selectedId: string | null
    ): TemplateResult {
        const captionId = `context-org-group-${groupId}`;

        return html`
            <div class="context-dropdown-group" role="group" aria-labelledby=${captionId}>
                <div class="context-dropdown-caption" id=${captionId}>${label}</div>
                ${organizations.map(org => this.renderOrgDropdownItem(org, selectedId))}
            </div>
        `;
    }

    /**
     * Renders a single organisation entry in the dropdown.
     * @param org - The organisation to render.
     * @param selectedId - The active organisation id, or null for "Alle".
     * @returns The organisation button.
     */
    private renderOrgDropdownItem (
        org: IOrganizationListItem,
        selectedId: string | null
    ): TemplateResult {
        return html`
            <button class="context-dropdown-item"
                    ?data-selected=${org.id === selectedId}
                    ?data-inactive=${!org.active}
                    aria-current=${org.id === selectedId ? 'true' : nothing}
                    @click=${(): void => this.navigate(`/organizations/${org.id}`)}>
                <span class="context-dropdown-item-bullet"></span>
                <span class="context-dropdown-item-label">${org.nameWithLegalForm}</span>
            </button>
        `;
    }

    // ─── Farm context ───────────────────────────────────────────────

    private get farmPage (): IFarmSwitcherPage | null {
        return this.farmsTask.value ?? null;
    }

    /**
     * Resolves the open farm, preferring the switcher's slice over the entity
     * `activeFarmTask` holds. The slice is consulted first because it is cached
     * per scope: farm-to-farm navigation within one organisation resolves from
     * it synchronously, where the entity would have the row fall back to
     * "Kein Betrieb ausgewählt" for the length of a round trip.
     *
     * The entity carries the open farm past the cut. Its id is re-checked
     * because `@lit/task` keeps the previous value while the next run is
     * pending, which would otherwise name the previously open farm.
     * @returns The open farm, or null when no farm is open or it resolves
     *   from neither source.
     */
    private get activeFarm (): SidebarFarm | null {
        const { farmId } = this.activeContext;

        if (farmId === null) {
            return null;
        }

        const fromPage = this.farmPage?.items.find(farm => farm.id === farmId);

        if (fromPage !== undefined) {
            return fromPage;
        }

        const entity = this.activeFarmTask.value ?? null;

        return entity?.id === farmId ? entity : null;
    }

    private get isCrossScopeFarmRow (): boolean {
        const { organizationId, farmId } = this.activeContext;

        return this.activeFarm === null && organizationId === null && farmId === null;
    }

    private renderFarmRow (): TemplateResult {
        const isActive = this.activeContext.level === 'farm';
        const farmName = this.getFarmRowName();
        const rowIcon = this.isCrossScopeFarmRow ? faListUl : faSeedling;

        return html`
            <button class="context-row"
                    ?data-active=${isActive}
                    aria-label=${`Betrieb: ${farmName}`}
                    aria-controls=${this.farmDropdownOpen ? 'context-dropdown-farm' : nothing}
                    aria-expanded=${this.farmDropdownOpen ? 'true' : 'false'}
                    @click=${this.handleFarmRowClick}>
                <fm-icon class="context-icon" .icon=${rowIcon} auto-width></fm-icon>
                <span class="context-row-name">${farmName}</span>
                <fm-icon class="context-row-chevron"
                         .icon=${this.farmDropdownOpen ? faAngleUp : faAngleDown}
                         auto-width></fm-icon>
            </button>
            ${this.farmDropdownOpen ? this.renderFarmDropdown() : nothing}
        `;
    }

    private getFarmRowName (): string {
        const active = this.activeFarm;

        if (active !== null) {
            return active.nameWithLegalForm;
        }

        const { organizationId, farmId } = this.activeContext;

        if (organizationId === null && farmId === null) {
            return 'Alle';
        }

        const page = this.farmPage;

        if (page !== null && page.total > 0) {
            return 'Kein Betrieb ausgewählt';
        }

        return 'Kein Betrieb';
    }

    private readonly handleFarmRowClick = (): void => {
        const page = this.farmPage;

        // Empty farm list: skip the dropdown (it would only carry
        // "Keine Betriebe" and "+ Betrieb anlegen") and navigate to the
        // empty-state page so the primary CTA sits in the main content
        // area. While the list is still loading the toggle falls through
        // so users see the loading spinner. An open farm keeps the
        // dropdown, because the entity puts it there even when the list
        // came back empty.
        if (page !== null && page.total === 0 && this.activeFarm === null) {
            this.navigate(this.getFarmListPath());

            return;
        }

        this.farmDropdownOpen = !this.farmDropdownOpen;
    };

    /**
     * The switcher's way out to the axis' complete, paginated farm list.
     *
     * The organisation's Betriebe tab is only offered when the caller can open
     * that organisation: `farmDetailPath` keeps a route organisation in the URL
     * for a farm-only member too, and that tab would reject them.
     * @returns The path of the farm list for the active axis.
     */
    private getFarmListPath (): string {
        const { organizationId } = this.activeContext;

        return farmsPath(this.activeOrganization === null ? null : organizationId);
    }

    /**
     * Builds a farm's detail path, keeping the active organisation scope in
     * the URL so a farm reached through a consulting organisation stays under
     * that organisation. In the cross-scope ("Alle") view the farm's managing
     * organisation supplies the scope; an orphan farm resolves to the
     * standalone path.
     * @param farm - The farm to navigate to.
     * @param scopeOrganizationId - The active organisation scope, or null for
     *   the cross-scope view.
     * @returns The farm detail path.
     */
    private farmDetailPath (farm: SidebarFarm, scopeOrganizationId: string | null): string {
        return farmPath(scopeOrganizationId ?? farm.managingOrganizationId, farm.id);
    }

    /**
     * Builds the dropdown's farm entries: the switcher's slice, led by the open
     * farm when the slice does not reach it. Beyond the cut the slice holds the
     * alphabetically first farms, so the open farm has no correct position
     * among them; leading the list keeps it reachable, and its selected marker
     * identifies it.
     * @param items - The loaded switcher slice.
     * @returns The entries to render, in display order.
     */
    private getFarmDropdownEntries (items: Array<IFarmListItem>): Array<SidebarFarm> {
        const active = this.activeFarm;

        if (active === null || items.some(farm => farm.id === active.id)) {
            return items;
        }

        return [
            active,
            ...items,
        ];
    }

    /**
     * The placeholder that terminates the farm run when the slice does not hold
     * every farm, carrying the entry marker so it reads as "more of the same"
     * rather than as a control. Disabled, so it takes no focus and no click,
     * and `aria-hidden` because the link below already announces the way out.
     * @param isCut - Whether farms exist beyond the slice.
     * @returns The placeholder entry, or nothing when the slice is complete.
     */
    private renderFarmEllipsis (isCut: boolean): TemplateResult | typeof nothing {
        if (!isCut) {
            return nothing;
        }

        return html`
            <button class="context-dropdown-item" disabled aria-hidden="true">
                <span class="context-dropdown-item-bullet"></span>
                <span class="context-dropdown-item-label">…</span>
            </button>
        `;
    }

    /**
     * The entry that leads out of the switcher when the slice does not hold
     * every farm. Only the axis' own list can page through the rest, so the
     * switcher stops listing and points there instead of growing a second
     * page navigation into a 224 px column.
     * @param isCut - Whether farms exist beyond the slice.
     * @returns The link entry, or nothing when the slice is complete.
     */
    private renderFarmListLink (isCut: boolean): TemplateResult | typeof nothing {
        if (!isCut) {
            return nothing;
        }

        return html`
            <button class="context-dropdown-item context-dropdown-item-more"
                    @click=${(): void => this.navigate(this.getFarmListPath())}>
                <fm-icon .icon=${faChevronRight} auto-width></fm-icon>
                <span class="context-dropdown-item-label">Alle Betriebe anzeigen</span>
            </button>
        `;
    }

    private renderFarmDropdown (): TemplateResult {
        const { organizationId, farmId } = this.activeContext;
        const page = this.farmPage;

        if (page === null) {
            return html`<div class="context-dropdown" id="context-dropdown-farm">
                <div class="context-dropdown-loading">
                    <wa-spinner></wa-spinner>
                </div>
            </div>`;
        }

        // The "Alle" entry is always present and always navigates to
        // `/farms` — the cross-scope farm list. Mirrors the organisation
        // dropdown's "Alle → /organizations" entry so the two switchers
        // behave consistently regardless of the currently active context.
        const allSelected = organizationId === null && farmId === null;
        const isCut = page.total > farmSwitcherLimit;

        return html`
            <div class="context-dropdown" id="context-dropdown-farm">
                <button class="context-dropdown-item"
                        ?data-selected=${allSelected}
                        aria-current=${allSelected ? 'true' : nothing}
                        @click=${(): void => this.navigate('/farms')}>
                    <fm-icon class="context-dropdown-item-icon" .icon=${faListUl} auto-width></fm-icon>
                    <span class="context-dropdown-item-label">Alle</span>
                </button>
                ${this.getFarmDropdownEntries(page.items).map(farm => html`
                    <button class="context-dropdown-item"
                            ?data-selected=${farm.id === farmId}
                            ?data-inactive=${!farm.active}
                            aria-current=${farm.id === farmId ? 'true' : nothing}
                            @click=${(): void => this.navigate(this.farmDetailPath(farm, organizationId))}>
                        <span class="context-dropdown-item-bullet"></span>
                        <span class="context-dropdown-item-label">${farm.nameWithLegalForm}</span>
                    </button>
                `)}
                ${this.renderFarmEllipsis(isCut)}
                ${this.renderFarmListLink(isCut)}
                ${this.renderCreateFarmEntry(organizationId)}
            </div>
        `;
    }

    /**
     * Whether a farm may be created under the active organisation: the caller can
     * commit one to it, and it is not deactivated, which the server answers with
     * `organizationInactive`. Both conditions the Betriebe tab's CTA carries as well,
     * the second one there as a disabled button with a tooltip.
     * @returns Whether the create would be accepted.
     */
    private get canCreateFarmInActiveOrganization (): boolean {
        return this.organizationWriteTask.value === true && this.activeOrganization?.active === true;
    }

    /**
     * The switcher's "Betrieb anlegen" entry, offered only where the create it leads
     * to would be accepted: `/farms/new` assigns no organisation and is open to every
     * caller, while the org-scoped route carries the two conditions
     * {@link canCreateFarmInActiveOrganization} answers.
     * @param organizationId - The active organisation, or null.
     * @returns The entry, or nothing where the create would be refused.
     */
    private renderCreateFarmEntry (organizationId: string | null): TemplateResult | typeof nothing {
        const scoped = organizationId !== null;

        if (scoped && !this.canCreateFarmInActiveOrganization) {
            return nothing;
        }

        return html`
            <button class="context-dropdown-item context-dropdown-item-add"
                    @click=${(): void => this.navigate(`${farmsPath(organizationId)}/new`)}>
                <fm-icon .icon=${faPlus} auto-width></fm-icon>
                <span class="context-dropdown-item-label">Betrieb anlegen</span>
            </button>
        `;
    }

    // ─── Fertilizer-year context ────────────────────────────────────

    /**
     * The fertilizer-years path of the open farm, keeping the active
     * organisation scope in the URL so a farm reached through a consulting
     * organisation stays under it.
     * @returns The path, or null when no farm is open.
     */
    private get fertilizerYearsPath (): string | null {
        const { organizationId, farmId } = this.activeContext;

        if (farmId === null) {
            return null;
        }

        return `${farmPath(organizationId, farmId)}/fertilizer-years`;
    }

    /**
     * The UUID of the open Düngejahr, which the route carries only as its four-digit year.
     *
     * Every year-scoped endpoint is addressed by the id, so the year segment has to be
     * resolved against the list the sidebar already holds. Null while that list has not
     * arrived yet, which is one of the two reasons a state can be unknown.
     * @returns The id, or null outside a year or before the list resolved.
     */
    private get openFertilizerYearId (): string | null {
        const { year } = this.activeContext;

        if (year === null) {
            return null;
        }

        return this.years?.find(entry => entry.year === year)?.id ?? null;
    }

    /**
     * Computes the year-row's data-state flags up-front. Splitting
     * this out keeps `renderYearRow` under the cyclomatic-complexity
     * threshold (max 7) once `aria-expanded` was added.
     *
     * @returns A bag of pre-computed flags consumed by `renderYearRow`
     *   to produce the various data-* and aria-* attributes.
     */
    private getYearRowState (): {
        noFarm: boolean;
        isActive: boolean;
        emptySoft: boolean;
        empty: boolean;
        dropdownOpen: boolean;
    } {
        const { farmId, year } = this.activeContext;
        const noFarm = farmId === null;
        const isActive = this.activeContext.level === 'year';

        return {
            noFarm,
            isActive,
            emptySoft: !isActive && noFarm,
            empty: !isActive && !noFarm && year === null,
            dropdownOpen: this.yearDropdownOpen && !noFarm,
        };
    }

    private renderYearRow (): TemplateResult {
        const yearName = this.getYearRowName();
        const rowState = this.getYearRowState();
        const ariaExpanded = rowState.dropdownOpen ? 'true' : 'false';
        const chevronIcon = this.yearDropdownOpen ? faAngleUp : faAngleDown;
        const dropdown = rowState.dropdownOpen ? this.renderYearDropdown() : nothing;

        return html`
            <button class="context-row"
                    ?data-active=${rowState.isActive}
                    ?data-empty-soft=${rowState.emptySoft}
                    ?data-empty=${rowState.empty}
                    aria-label=${`Düngejahr: ${yearName}`}
                    aria-controls=${rowState.dropdownOpen ? 'context-dropdown-year' : nothing}
                    aria-expanded=${ariaExpanded}
                    @click=${this.toggleYearDropdown}>
                <fm-icon class="context-icon" .icon=${faCalendarDays} auto-width></fm-icon>
                <span class="context-row-name">${yearName}</span>
                <fm-icon class="context-row-chevron"
                         .icon=${chevronIcon}
                         auto-width></fm-icon>
            </button>
            ${dropdown}
        `;
    }

    private getYearRowName (): string {
        const { farmId, year } = this.activeContext;

        if (farmId === null) {
            return 'Kein Betrieb ausgewählt';
        }

        if (year !== null) {
            return year;
        }

        // `years === null` means the list is still loading; fall through
        // to the neutral placeholder so the row doesn't flash a more
        // specific label that may turn out to be wrong once data arrives.
        if (this.years === null) {
            return 'Kein Düngejahr';
        }

        if (this.years.length === 0) {
            return 'Kein Düngejahr angelegt';
        }

        // Years exist for this farm but the URL has no `:year` segment —
        // mirrors the BETRIEB row's "Kein Betrieb ausgewählt" wording.
        return 'Kein Düngejahr ausgewählt';
    }

    private readonly toggleYearDropdown = (): void => {

        // Context-selector pattern: the row is a single toggle. When no farm
        // is selected there is nothing to choose from, so the click is a
        // no-op — matching the quiet visual state shown by `data-empty-soft`.
        if (this.activeContext.farmId === null) {
            return;
        }

        this.yearDropdownOpen = !this.yearDropdownOpen;
    };

    private readonly handleCreateFertilizerYearFromSidebar = (): void => {
        this.closeAllDropdowns();
        this.drawerOpen = false;

        const targetPath = this.fertilizerYearsPath;

        if (targetPath === null) {
            return;
        }

        if (window.location.pathname !== targetPath) {
            redirectTo(targetPath);
        }

        // Notify any mounted farm-detail component to open the create dialog.
        // Dispatched on `window` so it reaches pages rendered inside the slot.
        requestFertilizerYearCreation();
    };

    private renderYearDropdown (): TemplateResult {
        const { year } = this.activeContext;
        const yearBasePath = this.fertilizerYearsPath;

        if (yearBasePath === null) {
            return html``;
        }

        if (this.years === null) {
            return html`<div class="context-dropdown" id="context-dropdown-year">
                <div class="context-dropdown-loading">
                    <wa-spinner></wa-spinner>
                </div>
            </div>`;
        }

        return html`
            <div class="context-dropdown" id="context-dropdown-year">
                ${this.years.length === 0 ? html`
                    <span class="context-dropdown-empty">Keine Düngejahre</span>
                ` : this.years.map(entry => html`
                    <button class="context-dropdown-item"
                            ?data-selected=${entry.year === year}
                            aria-current=${entry.year === year ? 'true' : nothing}
                            @click=${(): void => this.navigate(`${yearBasePath}/${entry.year}`)}>
                        <span class="context-dropdown-item-bullet"></span>
                        <span class="context-dropdown-item-label">${entry.year}</span>
                    </button>
                `)}
                ${this.renderYearDropdownCreateCta()}
            </div>
        `;
    }

    /**
     * Renders the "+ Düngejahr anlegen" entry of the sidebar dropdown.
     *
     * Disable the create CTA on deactivated farms per the "Inactive
     * Entity CTAs — disable, not hide" rule (docs/design/pages.md). Existing year
     * entries in the parent dropdown stay clickable so users can still
     * view past years on a deactivated farm. Defaults to active while
     * the farm list is still loading, matching the convention used in
     * `farm_detail.ts`. The wrapper span receives hover/focus on behalf
     * of the disabled button so the explanatory tooltip can fire.
     * @returns The CTA + its tooltip as a single template fragment.
     */
    private renderYearDropdownCreateCta (): TemplateResult {
        const farmActive = this.activeFarm?.active ?? true;

        return html`
            <span class="context-dropdown-item-anchor"
                  id="fm-sidebar-create-year-cta"
                  tabindex=${farmActive ? '-1' : '0'}>
                <button class="context-dropdown-item context-dropdown-item-add"
                        ?disabled=${!farmActive}
                        @click=${this.handleCreateFertilizerYearFromSidebar}>
                    <fm-icon .icon=${faPlus} auto-width></fm-icon>
                    <span class="context-dropdown-item-label">Düngejahr anlegen</span>
                </button>
            </span>
            <wa-tooltip for="fm-sidebar-create-year-cta" ?disabled=${farmActive}>
                ${deactivatedFarmTooltip('Düngejahre anzulegen')}
            </wa-tooltip>
        `;
    }

    // ─── Fertilizer-year work areas ─────────────────────────────────

    /**
     * The work areas of the open fertilizer year, rendered from the shared
     * registry. Absent outside a year, where the entries would point at
     * paths that carry no year to work on.
     *
     * The indicator ahead of each label is the area's state, derived from the year's data by
     * {@link areaStatesTask} and never by this component. While that read is pending or has
     * failed, no area carries an indicator at all: the states arrive together or not at all,
     * and the empty glyph already means "no data" rather than "not known".
     *
     * What the three glyphs mean is `<fm-area-state-legend>`, right-aligned on the heading
     * by `.section-label--with-info` onto the chevrons of the context rows above, the two
     * sharing a horizontal padding. Only this heading carries one, which is why the rule is
     * a modifier rather than a change to every section label.
     * @returns The section, or nothing outside a year.
     */
    private renderAreaSection (): TemplateResult | typeof nothing {
        const { year } = this.activeContext;
        const yearsPath = this.fertilizerYearsPath;

        if (year === null || yearsPath === null) {
            return nothing;
        }

        const entries = this.visibleEntries(fertilizerYearAreaEntries(`${yearsPath}/${year}`));
        const states = this.areaStatesTask.render({
            complete: (answered): ReadonlyArray<IFertilizerYearAreaState> | null => answered,
            error: (): null => null,
            pending: (): null => null,
            initial: (): null => null,
        }) ?? null;

        return html`
            <div class="sidebar-section">
                <span class="section-label section-label--with-info">
                    Bereiche
                    <fm-area-state-legend></fm-area-state-legend>
                </span>
                <ul class="area-list" aria-label="Bereiche">
                    ${entries.map(entry => this.renderAreaEntry(entry, states))}
                </ul>
            </div>
        `;
    }

    /**
     * Renders one work area as a link, so it carries its target in the markup
     * and can be opened in a new tab like any other navigation target.
     *
     * The state reaches a screen reader as **words** through the link's own `aria-label`,
     * which is where the sidebar's context rows carry their state too. A visually-hidden
     * span after the label would do the same, but only with the whitespace the template puts
     * between two elements ("Schlagdaten , Vollständig"); the attribute names the
     * link exactly, and the visible label is contained in it (WCAG 2.5.3). The glyph stays
     * `aria-hidden`, so shape carries the state on screen and the words carry it to a screen
     * reader, and neither depends on colour.
     * @param entry - The area's navigation entry.
     * @param states - What the Düngejahr answered, or null while that is unknown.
     * @returns The entry's list item.
     */
    private renderAreaEntry (
        entry: INavigationEntry,
        states: ReadonlyArray<IFertilizerYearAreaState> | null
    ): TemplateResult {
        const isActive = isNavigationEntryActive(entry, this.currentPathname);
        const descriptor = states === null ? null : areaStateDescriptors[areaStateOf(states, entry.id)];

        return html`
            <li>
                <a class="area-row"
                   href=${entry.path}
                   ?data-active=${isActive}
                   aria-current=${isActive ? 'page' : nothing}
                   aria-label=${descriptor === null ? nothing : `${entry.label}, ${descriptor.label}`}
                   @click=${(event: MouseEvent): void => this.handleAreaClick(event, entry.path)}>
                    ${descriptor === null ? html`

                        <!-- Holds the indicator column while no state is known: the row is
                             a two-column grid, and an empty first cell would drop the label
                             into the 14 px track. Carries no content, so it announces
                             nothing. -->
                        <span class="area-status"></span>
                    ` : html`
                        <fm-icon class="area-status" .icon=${descriptor.icon} auto-width></fm-icon>
                    `}
                    <span class="area-row-name">${entry.label}</span>
                </a>
            </li>
        `;
    }

    /**
     * Opens a work area, which starts it rather than returns to it: every remembered
     * page is dropped, so the area opens on its first one.
     *
     * **Dropped only where the area is actually opened.** The entry of the area already on
     * screen navigates nowhere, so no list is mounted again and none would read the
     * emptied memory; forgetting there would take every other Betrieb's remembered page
     * with it and move nothing the reader can see.
     * @param event - The click on the entry.
     * @param path - The area to open.
     */
    private readonly handleAreaClick = (event: MouseEvent, path: string): void => {
        if (!isPlainFollow(event)) {
            return;
        }

        event.preventDefault();

        if (window.location.pathname === path) {
            this.dismissNavigation();

            return;
        }

        forgetListPages();
        this.navigate(path);
    };

    /**
     * The context selectors and the work areas, rendered into the sidebar above
     * the breakpoint and into the drawer below it, never into both: the two
     * surfaces share one shadow root, and a second copy would give every id in
     * here a twin that `getElementById` reaches first, leaving a tooltip anchored
     * to the hidden one.
     * @returns The navigation for the tier in play.
     */
    private renderSidebarNav (): TemplateResult {

        // The <nav> is named so screen-reader landmark navigation
        // can distinguish it from the breadcrumb <nav> further inside
        // the page (WCAG 2.4.6 Headings and Labels).
        //
        // Each context-row button under this nav carries a dynamic
        // `aria-label` of the form "Organisation: <name>" /
        // "Betrieb: <name>" / "Düngejahr: <year>" so AT users hear
        // the selector category alongside the current value (WCAG
        // 4.1.2 Name, Role, Value). The <name> mirrors the visible
        // label including the legal-form suffix so the accessible name
        // contains the visible text (WCAG 2.5.3 Label in Name). The
        // visual section labels are kept for sighted users.
        return html`
            <div class="sidebar-scroll">
                <nav class="sidebar-nav" aria-label="Hauptnavigation">
                    <div class="sidebar-section">
                        <span class="section-label">Organisation</span>
                        ${this.renderOrganizationRow()}
                    </div>
                    <div class="sidebar-section">
                        <span class="section-label">Betrieb</span>
                        ${this.renderFarmRow()}
                    </div>
                    <div class="sidebar-section">
                        <span class="section-label">Düngejahr</span>
                        ${this.renderYearRow()}
                    </div>
                    ${this.renderAreaSection()}
                    <span class="version">${versionLabel}</span>
                </nav>
            </div>
        `;
    }

    private renderUserTrigger (initials: string, displayName: string): TemplateResult {
        const hasPending = this.pendingInvitationCount > 0;
        const ariaLabel = hasPending ?
            `Benutzermenü ${initials} ${displayName}, offene Einladungen vorhanden` :
            `Benutzermenü ${initials} ${displayName}`;

        return html`
            <!--
                aria-label combines the two visible labels
                ("AS" avatar initials + the display name) and
                adds the "Benutzermenü" purpose prefix so the
                accessible name announces both the visible
                content (WCAG 2.5.3 Label in Name) and what
                activating the button does. On mobile the
                display-name span is hidden via CSS, but the
                aria-label remains complete. The notification
                dot itself stays decorative because the trigger's
                aria-label already surfaces the "offene Einladungen"
                signal — duplicating it via dot-label would
                produce a double announcement.
            -->
            <button id="fm-user-menu-trigger" class="user-menu-trigger"
                    aria-haspopup="dialog"
                    aria-expanded=${this.userMenuOpen ? 'true' : 'false'}
                    aria-label=${ariaLabel}>
                <fm-notification-dot ?active=${hasPending}>
                    <span class="avatar">${initials}</span>
                </fm-notification-dot>
                <span class="user-name">${displayName}</span>
                <fm-icon class="user-chevron" .icon=${faAngleDown}></fm-icon>
            </button>
        `;
    }

    /**
     * The pending-invitation count that follows the Einladungen label.
     * @returns The counter badge.
     */
    private renderInvitationCount (): TemplateResult {
        const count = this.pendingInvitationCount;
        const ariaLabel = countedNoun(count, pendingInvitationNoun);

        return html`
            <fm-counter-badge .count=${count}
                              aria-label=${ariaLabel}></fm-counter-badge>
        `;
    }

    /**
     * Renders one account-popover entry. These stay buttons rather than
     * links, because they dismiss the popover through the
     * `data-popover="close"` contract they share with the sign-out control.
     * @param entry - The entry to render.
     * @returns The entry's button.
     */
    private renderAccountMenuEntry (entry: INavigationEntry): TemplateResult {
        const isActive = isNavigationEntryActive(entry, this.currentPathname);

        return html`
            <button class="user-menu-item" data-popover="close"
                    @click=${(): void => this.handleNavClick(entry.path)}
                    aria-current=${isActive ? 'page' : nothing}>
                ${entry.label}
                ${entry.id === invitationsEntryId ? this.renderInvitationCount() : nothing}
            </button>
        `;
    }

    private renderUserMenu (displayName: string, email: string, initials: string): TemplateResult {
        return html`
            ${this.renderUserTrigger(initials, displayName)}
            <wa-popover class="user-menu-popover" for="fm-user-menu-trigger"
                        placement="bottom-end" without-arrow
                        @keydown=${this.userMenuKeydownListener}
                        @wa-show=${this.handleUserMenuShow}
                        @wa-after-show=${this.handleUserMenuAfterShow}
                        @wa-after-hide=${this.handleUserMenuAfterHide}>
                <div class="dropdown-user-info">
                    <div class="dropdown-user-name">${displayName}</div>
                    <div class="dropdown-user-email">${email}</div>
                </div>
                <wa-divider></wa-divider>
                <div class="dropdown-design-section">
                    <div class="dropdown-design-caption">DESIGN</div>
                    <fm-color-scheme-switcher mode="labeled"></fm-color-scheme-switcher>
                </div>
                <wa-divider></wa-divider>
                <nav class="user-menu-nav" aria-label="Benutzermenü">
                    ${this.visibleEntries(accountMenuEntries)
                        .map(entry => this.renderAccountMenuEntry(entry))}
                    <wa-divider></wa-divider>
                    <button class="user-menu-item user-menu-item--danger"
                            data-popover="close" @click=${this.handleLogout}>
                        Abmelden
                    </button>
                </nav>
            </wa-popover>
        `;
    }

    private readonly handlePageSlotChange = (event: Event): void => {
        const slot = event.target;

        if (!(slot instanceof HTMLSlotElement)) {
            return;
        }

        const scrolledItself = this.pageScrollsItself;
        const page = slot.assignedElements().find(candidate => candidate.hasAttribute('data-scrolls-itself'));
        this.pageScrollsItself = page !== undefined;
        this.followContentFocus(page instanceof HTMLElement ? page : null, scrolledItself);
    };

    /**
     * Keeps the content focus with whoever scrolls. The main area can hold it when a page that
     * scrolls itself arrives, after a click into it or on the browser's way back through the
     * history, so the page takes it over on arrival, and gives it back when it leaves with the
     * focus still on it.
     * @param page - The slotted page that scrolls itself, or null when none does.
     * @param scrolledItself - Whether the previous assignment had one.
     */
    private followContentFocus (page: HTMLElement | null, scrolledItself: boolean): void {
        if (page !== null) {
            this.handFocusToArrivingPage(page);

            return;
        }

        if (scrolledItself && document.activeElement === document.body) {
            this.mainContent()?.focus();
        }
    }

    private handFocusToArrivingPage (page: HTMLElement): void {
        const main = this.mainContent();

        if (main !== null && this.shadowRoot?.activeElement === main) {
            page.focus();
        }
    }

    render (): TemplateResult {
        const displayName = formatDisplayName(this.userInfo);
        const initials = this.getInitials();
        const email = this.userInfo?.email ?? '';

        return html`
            <!-- Header -->
            <header class="header">
                <div class="header-left">
                    <button class="menu-icon"
                            id="fm-menu-button"
                            aria-label=${this.menuButtonLabel}
                            aria-expanded=${this.mobileTier ? String(this.drawerOpen) : nothing}
                            aria-haspopup=${this.mobileTier ? 'dialog' : nothing}
                            @click=${this.handleMenuButtonClick}>
                        <fm-menu-toggle-icon ?collapsed=${this.sidebarCollapsed}
                                             ?mobile=${this.mobileTier}></fm-menu-toggle-icon>
                    </button>
                    <wa-tooltip for="fm-menu-button" placement="bottom">
                        ${this.menuButtonLabel}
                    </wa-tooltip>
                    <a class="home-link" href="/home" @click=${this.handleHomeLinkClick}>
                        <fm-brand-mark class="logo"
                                       label="Landwirtschaftskammer Schleswig-Holstein Logo"></fm-brand-mark>
                        <span class="title">Düngeplanung</span>
                    </a>
                </div>
                <div class="header-right">
                    ${this.renderUserMenu(displayName, email, initials)}
                </div>
            </header>

            <!-- Body -->
            <div class="body">
                <aside class="sidebar"
                       ?data-collapsed=${this.sidebarCollapsed}
                       ?data-overlay-pinned=${this.anyDropdownOpen}
                       ?data-overlay-suppressed=${this.railOverlaySuppressed}
                       @focusin=${this.handleRailReturn}>
                    ${this.mobileTier ? nothing : this.renderSidebarNav()}
                </aside>

                <main class="main-content" tabindex="-1" ?data-page-scrolls=${this.pageScrollsItself}>
                    <slot @slotchange=${this.handlePageSlotChange}></slot>
                </main>
            </div>

            <!-- Drawer (mobile) -->
            <wa-drawer placement="start" ?open=${this.drawerOpen}
                       without-header light-dismiss
                       @wa-after-hide=${(event: Event): void => {
                            if (event.target === event.currentTarget) {
                                this.handleDrawerHide();
                                this.releaseContentFocus();
                            }
                        }}>
                <div class="drawer-content">
                    <div class="drawer-header">
                        <button id="fm-drawer-button"
                                class="menu-icon"
                                aria-label="Navigation schließen"
                                @click=${this.handleDrawerHide}>
                            <fm-icon .icon=${faBars}></fm-icon>
                        </button>
                        <wa-tooltip for="fm-drawer-button" placement="bottom">Navigation schließen</wa-tooltip>
                        <a class="home-link" href="/home" @click=${this.handleHomeLinkClick}>
                            <fm-brand-mark class="logo"></fm-brand-mark>
                            <span class="title">Düngeplanung</span>
                        </a>
                    </div>
                    ${this.mobileTier ? this.renderSidebarNav() : nothing}
                </div>
            </wa-drawer>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'fm-application-frame': FmApplicationFrame;
    }
}
